import { useQuery } from "@tanstack/react-query";
import CardReporting from "components/cards/CardReporting/CardReporting";
import { ChartWrapper, HorizontalBarChart } from "components/charts";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { RouteNames } from "enums";
import { FeatureFlags } from "featureFlags";
import { connect, type ConnectedProps, useSelector } from "react-redux";
import { type RootState } from "features/Application/globaltypes/redux";
import { dateRange30, noBarData, noData } from "features/Library/Common/utils/performanceUtils";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { useMemo } from "react";
import { sharedAccountReportingHorizontalBarProps } from "../../Content/shared";
import { fetchEngagementQuery } from "../../Content/queries/useFlowLineChartQuery";
import { useEmailEngagementQuery } from "../../queries/sharedQueries";
import {
  getCustomerPacksSummary,
  transformToOverviewCardReportingItems,
} from "../../Packs/queries/useCustomerPacksSummaryQuery";
import * as dashboardReportingService from "../../services/dashboardReportingService";
import { TrendingAssets } from "./Trending/TrendingAssets";
import { TrendingFlows } from "./Trending/TrendingFlows";
import { FlowGoals } from "./FlowGoals/FlowGoals";

import styles from "./overviewReport.module.scss";
import { MoboLink } from "components/MoboLink/MoboLink";

const filter = dateRange30();

const fetchTrendingFlowsQuery = async () => {
  const result = (await dashboardReportingService.fetchTrendingFlowsData()).data.slice(0, 5);
  return result.map((item) => {
    return {
      ...item,
      id: item.FlowId,
    };
  });
};

const fetchTrendingAssetsQuery = async () => {
  const result = (await dashboardReportingService.fetchTrendingAssetsData()).data.slice(0, 5);
  return result.map((item) => {
    return {
      ...item,
      id: item.ContentId,
    };
  });
};

export const ReportingOverview = ({ accountId }: PropsFromRedux) => {
  const customerId = useSelector((state: RootState) => state.userProfile.accountId);
  const isReportingOverviewEnabled = !!useFeatureFlag(FeatureFlags.ReportingOverview);

  const customerPacksSummaryQuery = useQuery({
    queryFn: getCustomerPacksSummary,
    queryKey: ["customerPacksSummary", customerId, filter],
    enabled: !!customerId && isReportingOverviewEnabled,
  });

  const cards = useMemo(() => {
    if (customerPacksSummaryQuery.data) {
      return transformToOverviewCardReportingItems(customerPacksSummaryQuery.data).map((card) => ({
        ...card,
        url: `/${RouteNames.reportingPacks}`,
      }));
    }
    return [];
  }, [customerPacksSummaryQuery.data]);

  const flowEngagementQuery = useQuery({
    queryKey: ["OverviewFlowReportEngagement", { ...filter, isDistinct: false }],
    queryFn: fetchEngagementQuery,
    enabled: isReportingOverviewEnabled,
  });

  const emailEngagementQuery = useEmailEngagementQuery({
    filter: { ...filter, isDistinct: false, type: "account" },
    enabled: isReportingOverviewEnabled,
  });

  const trendingFlowsQuery = useQuery({
    queryKey: ["TrendingFlows"],
    queryFn: fetchTrendingFlowsQuery,
    enabled: isReportingOverviewEnabled,
  });

  const trendingAssetsQuery = useQuery({
    queryKey: ["TrendingAssets"],
    queryFn: fetchTrendingAssetsQuery,
    enabled: isReportingOverviewEnabled,
  });

  if (!isReportingOverviewEnabled) {
    return (
      <div className={styles.reportUnavailable}>
        <ReportUnavailable />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h2>Flows With Objectives</h2>
      </div>
      <FlowGoals filter={filter} />
      <div className={styles.packsHeader}>
        <h2>Packs & Licensing</h2>
        <MoboLink to={`../${RouteNames.packs}`}>More Details</MoboLink>
      </div>
      <div className={styles.cards}>
        <RequestStatusRenderer state={customerPacksSummaryQuery.status}>
          <CardReporting items={cards} />
        </RequestStatusRenderer>
      </div>
      <div className={styles.header}>
        <h2>Content</h2>
      </div>
      <div className={styles.charts}>
        <MoboLink to={`../${RouteNames.contentFlows}`}>
          <MoboLink to={`../${RouteNames.contentFlows}`} onClick={(e) => e.stopPropagation()}>
            More Details
          </MoboLink>
          <ChartWrapper titles={["Flow Engagement"]}>
            <RequestStatusRenderer state={flowEngagementQuery.status}>
              {flowEngagementQuery.isSuccess && !noBarData(...flowEngagementQuery.data.barData.map((d) => d.value)) ? (
                <HorizontalBarChart
                  {...sharedAccountReportingHorizontalBarProps}
                  data={flowEngagementQuery.data.barData}
                />
              ) : (
                noData(filter)
              )}
            </RequestStatusRenderer>
          </ChartWrapper>
        </MoboLink>
        <MoboLink to={`../${RouteNames.contentEmails}`}>
          <MoboLink to={`../${RouteNames.contentEmails}`} onClick={(e) => e.stopPropagation()}>
            More Details
          </MoboLink>
          <ChartWrapper titles={["Email Engagement"]}>
            <RequestStatusRenderer state={emailEngagementQuery.status}>
              {emailEngagementQuery.isSuccess &&
              !noBarData(
                emailEngagementQuery.data.raw.Sends,
                emailEngagementQuery.data.raw.Opens,
                emailEngagementQuery.data.raw.Clicks,
              ) ? (
                <HorizontalBarChart
                  {...sharedAccountReportingHorizontalBarProps}
                  data={emailEngagementQuery.data.barData}
                />
              ) : (
                noData(filter)
              )}
            </RequestStatusRenderer>
          </ChartWrapper>
        </MoboLink>
      </div>
      <div className={styles.trending}>
        <RequestStatusRenderer state={trendingFlowsQuery.status}>
          {trendingFlowsQuery.isSuccess && <TrendingFlows trendingFlows={trendingFlowsQuery.data} filter={filter} />}
        </RequestStatusRenderer>
        <RequestStatusRenderer state={trendingAssetsQuery.status}>
          {trendingAssetsQuery.isSuccess && (
            <TrendingAssets trendingAssets={trendingAssetsQuery.data} filter={filter} />
          )}
        </RequestStatusRenderer>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
});

const connector = connect(mapStateToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(ReportingOverview);
export default ConnectedComponent;
